import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { importUsers, validateUserImport } from "../../api/apiClient";
import { parseImportFile } from "./parseImportFile";
import {
  IGNORE,
  IMPORT_FIELDS,
  IMPORT_ROLES,
  REQUIRED_FIELDS,
  buildRows,
  guessMapping,
  unmappedFields,
  validateRows,
} from "./userImportModel";

const STEPS = ["Upload file", "Map & fix columns", "Tenant & roles", "Done"];
const PAGE_SIZE = 25;
const SERVER_CHECK_DELAY_MS = 600;

function errorMessage(e, fallback) {
  return e?.response?.data?.message || e?.message || fallback;
}

/**
 * Bulk-creates staff accounts from a CSV/Excel file: the admin maps file columns to the required
 * fields, fixes highlighted cells, then picks the tenant, each user's roles and whether to email them.
 */
export default function UserImportDialog({
  open,
  onClose,
  onImported,
  tenants,
  isGlobalAdmin,
  managedTenantIds,
}) {
  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState([]);
  const [cells, setCells] = useState([]); // raw file data, edited in place
  const [mapping, setMapping] = useState([]); // field key (or IGNORE) per column
  const [roles, setRoles] = useState([]); // role list per row
  const [serverChecks, setServerChecks] = useState([]); // [{ errors, existing }] per row
  const [checking, setChecking] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [page, setPage] = useState(0);
  const [tenantId, setTenantId] = useState("");
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const checkSeq = useRef(0);

  const tenantChoices = useMemo(
    () => (tenants || []).filter((t) => !managedTenantIds || managedTenantIds.includes(String(t.id))),
    [tenants, managedTenantIds],
  );
  const lockedTenant = !isGlobalAdmin && managedTenantIds?.length === 1 ? managedTenantIds[0] : null;

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setFileName("");
    setHeaders([]);
    setCells([]);
    setMapping([]);
    setRoles([]);
    setServerChecks([]);
    setOnlyProblems(false);
    setPage(0);
    setTenantId(lockedTenant || "");
    setSendWelcomeEmail(true);
    setError(null);
    setSummary(null);
  }, [open, lockedTenant]);

  const missing = unmappedFields(mapping);
  const rows = useMemo(
    () => buildRows(cells, mapping).map((r, i) => ({ ...r, roles: roles[i] || [] })),
    [cells, mapping, roles],
  );
  const clientErrors = useMemo(() => validateRows(rows), [rows]);
  const rowErrors = (i) => ({ ...(serverChecks[i]?.errors || {}), ...clientErrors[i] });
  const problemRows = rows.map((_, i) => i).filter((i) => Object.keys(rowErrors(i)).length > 0);

  // Ask the backend about existing accounts (and anything else it rejects) once edits settle.
  useEffect(() => {
    if (step !== 1 || missing.length > 0 || rows.length === 0) return undefined;
    const seq = ++checkSeq.current;
    const payload = rows.map(({ roles: _roles, ...fields }) => fields);
    const timer = setTimeout(() => {
      setChecking(true);
      validateUserImport(payload)
        .then((checks) => {
          if (seq === checkSeq.current) setServerChecks(checks);
        })
        .catch((e) => {
          if (seq === checkSeq.current) setError(errorMessage(e, "Could not check the rows."));
        })
        .finally(() => {
          if (seq === checkSeq.current) setChecking(false);
        });
    }, SERVER_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, cells, mapping]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const parsed = await parseImportFile(file);
      setFileName(file.name);
      setHeaders(parsed.headers);
      setCells(parsed.rows);
      setMapping(guessMapping(parsed.headers));
      setRoles(parsed.rows.map(() => []));
      setServerChecks([]);
      setPage(0);
      setStep(1);
    } catch (err) {
      setError(errorMessage(err, "Could not read that file."));
    } finally {
      setBusy(false);
    }
  };

  const setColumnField = (col, field) => {
    // A field maps to one column: choosing it here releases it from any other column.
    setMapping((m) => m.map((f, i) => (i === col ? field : f === field && field !== IGNORE ? IGNORE : f)));
  };

  const editCell = (r, c, value) => {
    setCells((prev) => prev.map((row, i) => {
      if (i !== r) return row;
      const next = [...row];
      next[c] = value;
      return next;
    }));
  };

  const removeRow = (r) => {
    setCells((prev) => prev.filter((_, i) => i !== r));
    setRoles((prev) => prev.filter((_, i) => i !== r));
    setServerChecks((prev) => prev.filter((_, i) => i !== r));
  };

  const toggleRole = (r, role) => {
    setRoles((prev) => prev.map((list, i) => {
      if (i !== r) return list;
      return list.includes(role) ? list.filter((x) => x !== role) : [...list, role];
    }));
  };

  const setRoleForAll = (role, on) => {
    setRoles((prev) => prev.map((list) => {
      const without = list.filter((x) => x !== role);
      return on ? [...without, role] : without;
    }));
  };

  const handleImport = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await importUsers(tenantId, rows, sendWelcomeEmail);
      setSummary(result);
      setStep(3);
      onImported?.();
    } catch (e) {
      const serverRowErrors = e?.response?.data?.rowErrors;
      if (Array.isArray(serverRowErrors)) {
        // Something changed since the last check (e.g. an account was created meanwhile): show it on the grid.
        setServerChecks(rows.map((_, i) => serverRowErrors.find((x) => x.row === i) || serverChecks[i] || {}));
        setOnlyProblems(true);
        setPage(0);
        setStep(1);
      }
      setError(errorMessage(e, "Import failed."));
    } finally {
      setBusy(false);
    }
  };

  const visibleRows = (onlyProblems && step === 1 ? problemRows : rows.map((_, i) => i));
  const pageRows = visibleRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const pager = visibleRows.length > PAGE_SIZE && (
    <TablePagination
      component="div"
      count={visibleRows.length}
      page={Math.min(page, Math.max(0, Math.ceil(visibleRows.length / PAGE_SIZE) - 1))}
      onPageChange={(_, p) => setPage(p)}
      rowsPerPage={PAGE_SIZE}
      rowsPerPageOptions={[PAGE_SIZE]}
    />
  );
  const existingCount = serverChecks.filter((c) => c?.existing).length;
  const rowsWithoutRoles = rows.filter((r) => r.roles.length === 0).length;

  const canNext = step === 1
    ? missing.length === 0 && problemRows.length === 0 && rows.length > 0 && !checking
    : false;
  const canImport = step === 2 && !!tenantId && rowsWithoutRoles === 0 && !busy;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontFamily: "var(--font-display)" }}>Import Users</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((s) => (
            <Step key={s}>
              <StepLabel>{s}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography sx={{ mb: 1 }}>
              Upload a .csv or .xlsx file with one user per row and a header row.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Required for each user: {REQUIRED_FIELDS.map((f) => f.label).join(", ")}. Optional:{" "}
              {IMPORT_FIELDS.filter((f) => !f.required).map((f) => f.label).join(", ")}. Other columns can be ignored.
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
              disabled={busy}
            >
              Choose file
              <input
                hidden
                type="file"
                accept=".csv,.xlsx"
                data-testid="user-import-file-input"
                onChange={handleFile}
              />
            </Button>
          </Box>
        )}

        {step === 1 && (
          <>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {fileName} · {rows.length} row{rows.length !== 1 ? "s" : ""}
              </Typography>
              {missing.length > 0 ? (
                <Alert severity="warning" sx={{ py: 0 }}>
                  Map a column to: {missing.map((f) => f.label).join(", ")}
                </Alert>
              ) : problemRows.length > 0 ? (
                <Chip color="error" label={problemRows.length === 1 ? "1 row needs attention" : `${problemRows.length} rows need attention`} />
              ) : (
                <Chip color="success" label="All rows look good" />
              )}
              {existingCount > 0 && (
                <Chip color="info" variant="outlined" label={`${existingCount} already have an account — roles will be added`} />
              )}
              {checking && <CircularProgress size={18} />}
              <FormControlLabel
                sx={{ ml: "auto" }}
                control={<Switch checked={onlyProblems} onChange={(e) => { setOnlyProblems(e.target.checked); setPage(0); }} />}
                label="Only rows with problems"
              />
            </Box>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" data-testid="user-import-grid">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    {headers.map((h, c) => (
                      <TableCell key={c} sx={{ minWidth: 180, verticalAlign: "top" }}>
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {h}
                        </Typography>
                        <Select
                          size="small"
                          fullWidth
                          displayEmpty
                          value={mapping[c] ?? IGNORE}
                          onChange={(e) => setColumnField(c, e.target.value)}
                          inputProps={{ "aria-label": `Map column ${h}`, "data-testid": `column-map-${c}` }}
                        >
                          <MenuItem value={IGNORE}>
                            <em>Ignore</em>
                          </MenuItem>
                          {IMPORT_FIELDS.map((f) => (
                            <MenuItem key={f.key} value={f.key}>
                              {f.required ? f.label : `${f.label} (optional)`}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    ))}
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((r) => {
                    const errs = rowErrors(r);
                    return (
                      <TableRow key={r} data-testid={`import-row-${r}`}>
                        <TableCell>
                          {r + 1}
                          {serverChecks[r]?.existing && (
                            <Tooltip title="Already has an account — roles will be added">
                              <Chip size="small" label="Existing" color="info" variant="outlined" sx={{ ml: 1 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        {headers.map((h, c) => {
                          const field = mapping[c];
                          const msg = field ? errs[field] : undefined;
                          return (
                            <TableCell key={c} sx={{ bgcolor: msg ? (t) => alpha(t.palette.error.main, 0.08) : undefined }}>
                              <TextField
                                variant="standard"
                                size="small"
                                fullWidth
                                value={cells[r]?.[c] ?? ""}
                                onChange={(e) => editCell(r, c, e.target.value)}
                                error={!!msg}
                                helperText={msg}
                                disabled={!field}
                                inputProps={{ "aria-label": `Row ${r + 1} ${h}`, "data-testid": `cell-${r}-${c}` }}
                              />
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          <IconButton size="small" title="Remove row" aria-label={`Remove row ${r + 1}`} onClick={() => removeRow(r)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            {pager}
          </>
        )}

        {step === 2 && (
          <>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
              {lockedTenant ? (
                <TextField
                  label="Training Center (Tenant)"
                  size="small"
                  value={tenants?.find((t) => String(t.id) === lockedTenant)?.name || lockedTenant}
                  disabled
                  sx={{ minWidth: 260 }}
                />
              ) : (
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel id="import-tenant-label">Training Center (Tenant)</InputLabel>
                  <Select
                    labelId="import-tenant-label"
                    label="Training Center (Tenant)"
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                    inputProps={{ "data-testid": "import-tenant-select" }}
                  >
                    {tenantChoices.map((t) => (
                      <MenuItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Give every user
                </Typography>
                {IMPORT_ROLES.map((role) => {
                  const all = rows.length > 0 && rows.every((r) => r.roles.includes(role.value));
                  const some = rows.some((r) => r.roles.includes(role.value));
                  return (
                    <FormControlLabel
                      key={role.value}
                      control={
                        <Checkbox
                          size="small"
                          checked={all}
                          indeterminate={some && !all}
                          onChange={(e) => setRoleForAll(role.value, e.target.checked)}
                          inputProps={{ "data-testid": `bulk-role-${role.value}` }}
                        />
                      }
                      label={role.label}
                    />
                  );
                })}
              </Box>
            </Box>
            {rowsWithoutRoles > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {rowsWithoutRoles} user{rowsWithoutRoles !== 1 ? "s" : ""} still need at least one role.
              </Alert>
            )}
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" data-testid="user-import-roles">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    {IMPORT_ROLES.map((role) => (
                      <TableCell key={role.value} align="center">
                        {role.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((r) => (
                    <TableRow key={r} sx={{ bgcolor: rows[r].roles.length ? undefined : (t) => alpha(t.palette.warning.main, 0.08) }}>
                      <TableCell>
                        {rows[r].firstName} {rows[r].lastName}
                        {serverChecks[r]?.existing && (
                          <Chip size="small" label="Existing" color="info" variant="outlined" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>{rows[r].email}</TableCell>
                      {IMPORT_ROLES.map((role) => (
                        <TableCell key={role.value} align="center">
                          <Checkbox
                            size="small"
                            checked={rows[r].roles.includes(role.value)}
                            onChange={() => toggleRole(r, role.value)}
                            inputProps={{
                              "aria-label": `${role.label} for ${rows[r].email}`,
                              "data-testid": `row-role-${r}-${role.value}`,
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {pager}
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch
                  checked={sendWelcomeEmail}
                  onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                  inputProps={{ "data-testid": "welcome-email-toggle" }}
                />
              }
              label="Send welcome emails with sign-in instructions"
            />
            {!sendWelcomeEmail && (
              <Typography variant="body2" color="text.secondary">
                New users will not be notified. They can set a password with “Forgot password” on the sign-in page.
              </Typography>
            )}
          </>
        )}

        {step === 3 && summary && (
          <Box data-testid="import-summary">
            <Alert severity="success" sx={{ mb: 2 }}>
              Created {summary.created} new user{summary.created !== 1 ? "s" : ""}, added roles to {summary.updated}{" "}
              existing user{summary.updated !== 1 ? "s" : ""}. Welcome emails sent: {summary.emailsSent}.
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>Roles granted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(summary.rows || []).map((r) => (
                  <TableRow key={r.row}>
                    <TableCell>{r.email}</TableCell>
                    <TableCell>
                      {{ CREATED: "Created", ROLES_ADDED: "Roles added", UNCHANGED: "Already had these roles" }[r.status] || r.status}
                    </TableCell>
                    <TableCell>
                      {(r.rolesGranted || [])
                        .map((v) => IMPORT_ROLES.find((x) => x.value === v)?.label || v)
                        .join(", ") || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 3 ? (
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            {step > 0 && (
              <Button onClick={() => { setStep(step - 1); setPage(0); }} disabled={busy}>
                Back
              </Button>
            )}
            {step === 1 && (
              <Button variant="contained" onClick={() => { setStep(2); setPage(0); }} disabled={!canNext}>
                Next
              </Button>
            )}
            {step === 2 && (
              <Button variant="contained" onClick={handleImport} disabled={!canImport} data-testid="import-submit">
                {busy ? "Importing…" : `Import ${rows.length} user${rows.length !== 1 ? "s" : ""}`}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
